import type { SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  output,
  input,
  TemplateRef,
} from '@angular/core';

import type { Candidate } from '@core/candidate-types';
import { TranslatePipe } from '@core/i18n-legacy';
import type { InterviewResult, ResultGroup } from '@core/interview-results-types';
import { OfferKind } from '@core/offers-types';
import type { Placement } from '@core/placements-types';
import { TimeAgoAsyncPipe } from '@core/time-legacy';
import { BoxComponent, IconComponent, RtTooltip } from '@core/user-interface-legacy';

import { InterviewResultSummaryOverallItemComponent } from './interview-result-summary-overall-item/interview-result-summary-overall-item.component';
import { SummaryEvaluationMeterComponent } from '../../../../candidate-profile-placement/evaluation-meter/summary-evaluation-meter.component';
import { InterviewResultsSummaryChartComponent } from '../../../../interview-results-summary-chart/interview-results-summary-chart.component';

@Component({
  selector: 'rt-interview-result-summary-overall',
  templateUrl: 'interview-result-summary-overall.component.html',
  styleUrls: ['./interview-result-summary-overall.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    InterviewResultsSummaryChartComponent,
    BoxComponent,
    IconComponent,
    RtTooltip,
    InterviewResultSummaryOverallItemComponent,
    TimeAgoAsyncPipe,
    TranslatePipe,
    SummaryEvaluationMeterComponent,
  ],
})
export class InterviewResultSummaryOverallComponent {
  public readonly candidate = input<Candidate>(undefined!);
  public readonly groups = input<ResultGroup[]>(undefined!);
  public readonly offerId = input<number>();
  public readonly currentUserAvailablePlacements = input<Placement[]>([]);
  public readonly hasAccessToAllJobOffers = input<boolean>();
  public readonly results = input<InterviewResult[]>([]);
  public readonly hiddenEvaluationsInfoboxTpl = input<TemplateRef<unknown>>();
  @Input() public lastUpdate: string | null;

  public summaryResultCounterClick = output();

  public uniqAdmins: number;
  public selectedPlacement?: Placement;
  public ratingVisible: boolean = true;
  public userEvaluatedCandidateInAtLeastOnePlacementWithFariEvaluationsEnabledAtCurrentStage = false;

  public ngOnInit(): void {
    this.setUniqAdmins();
    this.selectPlacement();
    this.setRatingVisibility();
    this.setUserEvaluatedCandidateInAtLeastOnePlacementWithFariEvaluationsEnabledAtCurrentStage();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.offerId || changes.lastUpdate?.currentValue !== changes.lastUpdate?.previousValue) {
      this.setUniqAdmins();
      this.selectPlacement();
      this.setRatingVisibility();
      this.setUserEvaluatedCandidateInAtLeastOnePlacementWithFariEvaluationsEnabledAtCurrentStage();
    }

    if (changes.candidate) {
      this.setUserEvaluatedCandidateInAtLeastOnePlacementWithFariEvaluationsEnabledAtCurrentStage();
      this.setRatingVisibility();
    }
  }

  public hasMultiplePlacements(): boolean {
    return this.candidate().placements.length > 1;
  }

  public trackByGroup(index: number, group: ResultGroup): string {
    return JSON.stringify(group);
  }

  public get positiveRatings(): number | null {
    if (this.selectedPlacement) {
      return this.selectedPlacement.ratingVisible ? this.selectedPlacement.positiveRatings! : null;
    }

    const candidate = this.candidate();
    return candidate.ratingVisible ? candidate.positiveRatings : null;
  }

  public get evaluationTranslationKey(): string {
    if (!this.offerId()) {
      return 'app.interview.average_evaluation_score';
    } else {
      return this.selectedPlacement!.offer?.kind === OfferKind.Job
        ? 'app.interview.evaluation_score'
        : 'app.interview.talent_pool_fit_score';
    }
  }

  private setRatingVisibility(): void {
    if (this.selectedPlacement) {
      this.ratingVisible = !!this.selectedPlacement.ratingVisible;
    } else {
      this.ratingVisible = this.candidate().ratingVisible;
    }
  }

  private selectPlacement() {
    this.selectedPlacement = this.offerId()
      ? this.candidate().placements.find(placement => placement.offerId === this.offerId())
      : undefined;
  }

  private setUniqAdmins() {
    this.uniqAdmins = new Set(
      this.results().map(result => result.admin?.id || result.guest?.id),
    ).size;
  }

  private setUserEvaluatedCandidateInAtLeastOnePlacementWithFariEvaluationsEnabledAtCurrentStage(): void {
    if (this.selectedPlacement) {
      this.userEvaluatedCandidateInAtLeastOnePlacementWithFariEvaluationsEnabledAtCurrentStage =
        this.selectedPlacement.stage &&
        this.selectedPlacement.stage.fairEvaluationsEnabled &&
        !!this.selectedPlacement.ratingVisible;
      return;
    }
    this.userEvaluatedCandidateInAtLeastOnePlacementWithFariEvaluationsEnabledAtCurrentStage =
      this.candidate().placements.some(
        placement =>
          placement.stage && placement.stage.fairEvaluationsEnabled && placement.ratingVisible,
      );
  }
}
